/**
 * Thin fetch wrapper for the admin backend.
 *
 *   - Always sends cookies (`credentials: include`)
 *   - Serializes JSON bodies and sets Content-Type automatically
 *   - Throws ApiError (preserves `status`) so TanStack Query can branch on auth failures
 *   - A global 401 listener (hooked up in `setup401Redirect`) pushes the user back to /login
 */

export interface ApiErrorBody {
  error?: string;
  message?: string;
  details?: unknown;
  requestId?: string;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: ApiErrorBody,
    public readonly raw?: unknown,
  ) {
    super(body?.message || body?.error || `HTTP ${status}`);
    this.name = 'ApiError';
  }
}

type On401 = (() => void) | null;
let on401: On401 = null;
export function setOn401Handler(fn: On401): void {
  on401 = fn;
}

export interface ApiFetchOptions extends RequestInit {
  json?: unknown;
}

export async function api<T = unknown>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const { json, headers, ...rest } = options;
  const finalHeaders = new Headers(headers);

  let body: BodyInit | undefined;
  if (json !== undefined) {
    finalHeaders.set('Content-Type', 'application/json');
    body = JSON.stringify(json);
  } else if (rest.body) {
    body = rest.body;
  }

  const resp = await fetch(path, {
    credentials: 'include',
    ...rest,
    headers: finalHeaders,
    body,
  });

  if (resp.status === 204) return undefined as T;

  const text = await resp.text();
  let parsed: unknown = text;
  if (text && resp.headers.get('Content-Type')?.includes('application/json')) {
    try {
      parsed = JSON.parse(text);
    } catch {
      /* leave raw */
    }
  }

  if (!resp.ok) {
    const errBody: ApiErrorBody =
      typeof parsed === 'object' && parsed !== null ? (parsed as ApiErrorBody) : { message: String(parsed) };
    if (resp.status === 401 && on401) on401();
    throw new ApiError(resp.status, errBody, text);
  }

  return parsed as T;
}

/* ---- Domain types (mirror of backend shapes) ---- */

export interface UserDetail {
  user: { id: string; phone: string | null; created_at?: string; last_sign_in_at?: string | null };
  brand: Brand | null;
  center: UserCenter;
}

export type RevokeUserSessionStatus = 'revoked' | 'invalid_phone' | 'not_found' | 'failed';

export interface RevokeUserSessionResult {
  input: string;
  phone: string | null;
  user_id: string | null;
  status: RevokeUserSessionStatus;
  message: string;
}

export interface RevokeUserSessionsBatchResult {
  results: RevokeUserSessionResult[];
  summary: Record<RevokeUserSessionStatus | 'total', number>;
}

export interface Brand {
  code: string;
  name: string;
  is_active: boolean;
  sort_order: number;
  metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface UserBrandAssignment {
  user_id: string;
  brand_code: string;
  source: 'admin' | 'provision' | 'backfill' | 'system';
  note: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
  brand?: Brand | null;
}

export interface SalesChannel {
  id: string;
  code: string;
  name: string;
  badge_label: string;
  icon: string;
  color: string;
  description: string | null;
  is_active: boolean;
  sort_order: number;
  metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface UserChannelAssignment {
  user_id: string;
  channel_id: string | null;
  source: 'activation' | 'admin' | 'default' | 'import' | 'system';
  note: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
  channel?: SalesChannel | null;
}

export interface UsageEvent {
  id: string;
  model: string;
  input_tokens: number;
  output_tokens: number;
  credits: number;
  status: string;
  seat_id: string | null;
  ip_id: string | null;
  idempotency_key: string;
  created_at: string;
}

export interface RoutingRule {
  id: string;
  from_model: string;
  to_model: string;
  user_id: string | null;
  user_phone?: string | null;
  priority: number;
  is_active: boolean;
  note: string | null;
  expires_at: string | null;
  created_at: string;
}

export interface OperationLogRow {
  id: string;
  operator: string;
  command: string;
  target_type: string | null;
  target_id: string | null;
  params: Record<string, unknown> | null;
  result: unknown;
  status: 'success' | 'failed' | 'dry_run';
  created_at: string;
}

export interface ModelPricingFull {
  id: string;
  model: string;
  cost_usd_per_1m_input_uncached: number;
  cost_usd_per_1m_input_cached: number;
  cost_usd_per_1m_output: number;
  is_active: boolean;
  updated_at?: string;
}

export interface ModelPricingHistoryRow {
  id: number;
  model_pricing_id: string;
  model: string;
  cost_usd_per_1m_input_uncached: number | null;
  cost_usd_per_1m_input_cached: number | null;
  cost_usd_per_1m_output: number | null;
  is_active: boolean | null;
  changed_at: string;
  changed_by: string | null;
  change_kind: 'insert' | 'update' | 'delete';
}

export interface PricingPreview {
  model: string;
  days: number;
  patch: Record<string, unknown>;
  n_events: number;
  n_users_affected: number;
  credits_old_total: number;
  credits_new_total: number;
  credits_delta: number;
  credits_delta_pct: number | null;
}

export interface CostByModelRow {
  model: string;
  input_tokens: number;
  cached_tokens: number;
  output_tokens: number;
  credits: number;
  cost_usd: number;
  request_count: number;
  unique_users: number;
}

export interface CostByUserRow {
  user_id: string;
  user_phone?: string | null;
  input_tokens: number;
  cached_tokens: number;
  output_tokens: number;
  credits: number;
  cost_usd: number;
  request_count: number;
}

export interface DailyAggRow {
  day: string;
  input_tokens: number;
  cached_tokens: number;
  output_tokens: number;
  credits: number;
  cost_usd: number;
  request_count: number;
  unique_users: number;
}

export interface CostSummary {
  from: string;
  to: string;
  total_users: number;
  total_requests: number;
  total_input_tokens: number;
  total_cached_tokens: number;
  total_output_tokens: number;
  total_credits: number;
  total_cost_usd: number;
  cache_hit_pct: number;
}

export interface UsageEventGlobal {
  id: string;
  user_id: string;
  user_phone?: string | null;
  seat_id?: string | null;
  ip_id?: string | null;
  model: string;
  input_tokens: number;
  output_tokens: number;
  credits: number;
  status: 'completed' | 'failed' | 'aborted';
  hui_request_id: string | null;
  idempotency_key: string;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

export interface SeatIp {
  id: string;
  user_id: string;
  name: string;
  description: string;
  status: 'active' | 'deleted';
  last_bound_seat_id: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export type SeatStatus = 'active' | 'expired' | 'suspended' | 'revoked';
export type SeatSource = 'default' | 'admin' | 'application' | 'manual';

export interface UserSeat {
  id: string;
  user_id: string;
  status: SeatStatus;
  source: SeatSource;
  valid_from: string;
  valid_until: string;
  credits_granted: number;
  credits_used_cached: number;
  current_ip_id: string | null;
  current_ip?: SeatIp | null;
  application_id: string | null;
  channel_id: string | null;
  channel?: SalesChannel | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  user?: AdminSeatUser | null;
}

export interface SeatApplication {
  id: string;
  user_id: string;
  status: 'pending' | 'approved' | 'rejected' | 'canceled';
  requested_credits: number | null;
  note: string | null;
  admin_note: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  approved_seat_id: string | null;
  created_at: string;
  updated_at: string;
  user?: AdminSeatUser | null;
}

export interface AdminSeatUser {
  id: string;
  phone: string | null;
}

export interface PaginatedResult<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
}

export interface SeatCreditLedgerRow {
  id: string;
  user_id: string;
  seat_id: string;
  op_type: string;
  delta_credits: number;
  credits_after: number;
  operator: string;
  note: string | null;
  application_id: string | null;
  created_at: string;
}

export interface SeatUsageEvent {
  id: string;
  seat_id: string | null;
  ip_id: string | null;
  model: string;
  input_tokens: number;
  output_tokens: number;
  credits: number;
  status: string;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

export interface UserCenter {
  channel: SalesChannel | null;
  seats: UserSeat[];
  ips: SeatIp[];
  applications: SeatApplication[];
  ledger: SeatCreditLedgerRow[];
  usage_events: SeatUsageEvent[];
  summary: {
    total_seats: number;
    bound_seats: number;
    available_seats: number;
    credits_total: number;
    credits_used: number;
    credits_remaining: number;
  };
}

/* ---- Typed wrappers by resource ---- */

function qs(params: Record<string, string | number | boolean | undefined>): string {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return parts.length ? `?${parts.join('&')}` : '';
}

export const adminApi = {
  // auth
  login: (token: string) => api<{ ok: true; expires_in: number }>('/admin/auth/login', { method: 'POST', json: { token } }),
  logout: () => api<{ ok: true }>('/admin/auth/session', { method: 'DELETE' }),
  whoami: () => api<{ ok: true; operator: string }>('/admin/auth/whoami'),

  // users
  getUser: (key: string) => api<UserDetail>(`/admin/users/${encodeURIComponent(key)}`),
  userHistory: (key: string, limit = 50) =>
    api<UsageEvent[]>(`/admin/users/${encodeURIComponent(key)}/history${qs({ limit })}`),
  revokeUserSessions: (input: { phones: string[] }) =>
    api<RevokeUserSessionsBatchResult>('/admin/users/revoke-sessions', { method: 'POST', json: input }),
  assignUserChannel: (key: string, input: { channel_id: string | null; note?: string }) =>
    api<{ assignment: UserChannelAssignment | null; channel: SalesChannel | null }>(
      `/admin/users/${encodeURIComponent(key)}/channel`,
      { method: 'PATCH', json: input },
    ),
  assignUserBrand: (key: string, input: { brand_code: string; note?: string }) =>
    api<{ assignment: UserBrandAssignment; brand: Brand | null }>(
      `/admin/users/${encodeURIComponent(key)}/brand`,
      { method: 'PATCH', json: input },
    ),

  // brands
  listBrands: (filter: { include_inactive?: boolean } = {}) =>
    api<Brand[]>(`/admin/brands${qs(filter)}`),

  // channels
  listChannels: (filter: { include_inactive?: boolean } = {}) =>
    api<SalesChannel[]>(`/admin/channels${qs(filter)}`),
  createChannel: (input: {
    code?: string;
    name: string;
    badge_label?: string;
    icon?: string;
    color?: string;
    description?: string;
    sort_order?: number;
    is_active?: boolean;
  }) => api<SalesChannel>('/admin/channels', { method: 'POST', json: input }),
  updateChannel: (id: string, patch: Partial<{
    code: string;
    name: string;
    badge_label: string;
    icon: string;
    color: string;
    description: string;
    sort_order: number;
    is_active: boolean;
  }>) => api<SalesChannel>(`/admin/channels/${encodeURIComponent(id)}`, { method: 'PATCH', json: patch }),
  deleteChannel: (id: string) =>
    api<{ deleted: SalesChannel }>(`/admin/channels/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  // routes
  listRoutes: () => api<RoutingRule[]>('/admin/routes'),
  addRoute: (input: {
    from_model: string; to_model: string; user_id?: string | null;
    priority?: number; note?: string; expires_at?: string | null;
  }) => api<RoutingRule>('/admin/routes', { method: 'POST', json: input }),
  toggleRoute: (id: string, isActive: boolean) =>
    api<RoutingRule>(`/admin/routes/${encodeURIComponent(id)}`, { method: 'PATCH', json: { is_active: isActive } }),
  deactivateAllRoutes: () => api<{ affected: number }>('/admin/routes?confirm=yes', { method: 'DELETE' }),

  // logs
  listLogs: (filter: Partial<{
    operator: string; target_type: string; target_id: string;
    status: 'success' | 'failed' | 'dry_run'; command: string;
    from: string; to: string; limit: number;
  }> = {}) => api<OperationLogRow[]>(`/admin/logs${qs(filter as Record<string, string | number | boolean | undefined>)}`),

  // config
  reloadConfig: () => api<{ ok: true }>('/admin/config/reload', { method: 'POST' }),

  // pricing
  listPricing: () => api<ModelPricingFull[]>('/admin/pricing'),
  createPricing: (input: {
    model: string;
    cost_usd_per_1m_input_uncached: number;
    cost_usd_per_1m_input_cached: number;
    cost_usd_per_1m_output: number;
    is_active?: boolean;
  }) => api<ModelPricingFull>('/admin/pricing', { method: 'POST', json: input }),
  updatePricing: (model: string, patch: Partial<{
    cost_usd_per_1m_input_uncached: number;
    cost_usd_per_1m_input_cached: number;
    cost_usd_per_1m_output: number;
    is_active: boolean;
  }>) => api<ModelPricingFull>(`/admin/pricing/${encodeURIComponent(model)}`, { method: 'PATCH', json: patch }),
  previewPricing: (model: string, patch: Record<string, unknown>) =>
    api<PricingPreview>(`/admin/pricing/${encodeURIComponent(model)}/preview`, { method: 'POST', json: patch }),
  listPricingHistory: (filter: { model?: string; limit?: number } = {}) =>
    api<ModelPricingHistoryRow[]>(`/admin/pricing/history${qs(filter)}`),

  // cost
  costByModel: () => api<CostByModelRow[]>('/admin/cost/by-model'),
  costByUser: (filter: { from?: string; to?: string; limit?: number } = {}) =>
    api<CostByUserRow[]>(`/admin/cost/by-user${qs(filter)}`),
  costDaily: (filter: { from?: string; to?: string } = {}) =>
    api<DailyAggRow[]>(`/admin/cost/daily${qs(filter)}`),
  costSummary: (filter: { from?: string; to?: string } = {}) =>
    api<CostSummary>(`/admin/cost/summary${qs(filter)}`),

  // events (global usage_events browser)
  listEvents: (filter: Partial<{
    user: string;
    model: string;
    status: 'completed' | 'failed' | 'aborted';
    from: string;
    to: string;
    limit: number;
  }> = {}) =>
    api<UsageEventGlobal[]>(`/admin/events${qs(filter as Record<string, string | number | boolean | undefined>)}`),

  // seats
  listSeats: (filter: {
    page?: number;
    page_size?: number;
    phone?: string;
    q?: string;
    status?: SeatStatus | '';
    source?: SeatSource | '';
  } = {}) =>
    api<PaginatedResult<UserSeat>>(`/admin/seats${qs(filter)}`),
  releaseSeatBinding: (seatId: string, input: { user_id: string; ip_id: string; reason?: string }) =>
    api<{ released: boolean; seat_id: string | null; next_active_ip_id: string | null }>(
      `/admin/seats/${encodeURIComponent(seatId)}/release`,
      { method: 'POST', json: input },
    ),
  renewSeat: (seatId: string, input: { user_id: string; months: number; credits?: number }) =>
    api<UserSeat>(`/admin/seats/${encodeURIComponent(seatId)}/renew`, { method: 'POST', json: input }),
  setSeatStatus: (seatId: string, input: { user_id: string; status: 'active' | 'suspended' }) =>
    api<UserSeat>(`/admin/seats/${encodeURIComponent(seatId)}/status`, { method: 'POST', json: input }),
  deleteSeat: (seatId: string, input: { user_id: string }) =>
    api<{ deleted: boolean; seat_id: string }>(`/admin/seats/${encodeURIComponent(seatId)}`, { method: 'DELETE', json: input }),
  setSeatChannel: (seatId: string, input: { user_id: string; channel_id: string | null }) =>
    api<UserSeat>(`/admin/seats/${encodeURIComponent(seatId)}/channel`, { method: 'POST', json: input }),
  listSeatApplications: (filter: { status?: SeatApplication['status'] | ''; page?: number; page_size?: number; phone?: string } = {}) =>
    api<PaginatedResult<SeatApplication>>(`/admin/seats/applications${qs(filter)}`),
  createSeat: (input: { user: string; credits: number; note: string }) =>
    api<UserSeat>('/admin/seats', { method: 'POST', json: input }),
  adjustSeatCredits: (seatId: string, input: { user_id: string; mode: 'grant' | 'deduct' | 'set'; amount: number; note: string }) =>
    api<UserSeat>(`/admin/seats/${encodeURIComponent(seatId)}/credits`, { method: 'POST', json: input }),
  approveSeatApplication: (id: string, input: { credits: number; admin_note: string }) =>
    api<{ application: SeatApplication; seat: UserSeat }>(
      `/admin/seats/applications/${encodeURIComponent(id)}/approve`,
      { method: 'POST', json: input },
    ),
  rejectSeatApplication: (id: string, input: { admin_note: string }) =>
    api<SeatApplication>(`/admin/seats/applications/${encodeURIComponent(id)}/reject`, {
      method: 'POST',
      json: input,
    }),

  // settings — runtime-tunable globals (FX rate etc.)
  getFxRate: () => api<FxSetting>('/admin/settings/fx'),
  setFxRate: (value: number) =>
    api<FxSetting>('/admin/settings/fx', { method: 'PATCH', json: { value } }),
  refreshFxRate: () =>
    api<{ rate: number; source: string; fetched_at: string }>('/admin/settings/fx/refresh', {
      method: 'POST',
    }),
  getDefaultSeatCredits: () =>
    api<NumberSetting>('/admin/settings/default-seat-credits'),
  setDefaultSeatCredits: (value: number) =>
    api<NumberSetting>('/admin/settings/default-seat-credits', { method: 'PATCH', json: { value } }),
  getDesktopMinVersion: () =>
    api<DesktopMinVersionSetting>('/admin/settings/desktop-min-version'),
  setDesktopMinVersion: (value: string) =>
    api<DesktopMinVersionSetting>('/admin/settings/desktop-min-version', { method: 'PATCH', json: { value } }),
};

export interface FxSetting {
  key: 'cny_per_usd';
  value: number;
  description: string | null;
  updated_at: string;
  updated_by: string | null;
}

export interface NumberSetting {
  key: string;
  value: number;
  description: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

export interface DesktopMinVersionSetting {
  key: 'desktop_min_version';
  value: string;
  latestVersion: string;
  description: string | null;
  updated_at: string | null;
  updated_by: string | null;
}
